import DOMPurify from 'dompurify';
import { safeHttpUrl } from '@/utils/citation-utils';


/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param dirty - The potentially unsafe HTML content
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  
  // Configure DOMPurify hooks for additional security
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Set all links to open in new tab with security attributes
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
      
      // Validate href to prevent javascript: and data: URLs
      const href = node.getAttribute('href');
      if (href && !isValidUrl(href)) {
        node.removeAttribute('href');
      }
    }

    // data-href is not a navigation surface we trust. Drop it so an
    // attacker-controlled javascript: value cannot survive sanitization.
    if (node.hasAttribute('data-href')) {
      node.removeAttribute('data-href');
    }

    // AppShell and citation chips read data-web-url as a navigation target.
    // Keep only http(s) URLs; drop javascript: and other schemes.
    if (node.hasAttribute('data-web-url')) {
      const cleaned = safeHttpUrl(node.getAttribute('data-web-url') || '');
      if (cleaned) {
        node.setAttribute('data-web-url', cleaned);
      } else {
        node.removeAttribute('data-web-url');
      }
    }
    
    // Remove any remaining dangerous attributes (except citation data attributes)
    const dangerousAttrs = ['style', 'onload', 'onerror'];
    dangerousAttrs.forEach(attr => {
      node.removeAttribute(attr);
    });
  });
  
  // Perform sanitization with custom configuration to allow citation elements
  const clean = DOMPurify.sanitize(dirty, {
    ADD_TAGS: ['button', 'span'], // Allow button and span for citations
    ADD_ATTR: ['data-cite', 'data-doc-guid', 'data-docket-entry-guid', 'data-chunk-id', 'data-cell-range', 'data-citation-key', 'data-citation-state', 'data-display-number', 'data-file-name', 'data-page-number', 'data-paragraph-number', 'data-position', 'data-segment-index', 'data-web-url', 'data-site-name', 'class', 'href', 'title'], // Allow citation data attributes including chunk ID and range
  });
  
  // Remove hooks after use to prevent memory leaks
  DOMPurify.removeHook('afterSanitizeAttributes');
  
  return clean;
}

/**
 * Validates if a URL is safe
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
function isValidUrl(url: string): boolean {
  if (!url) return false;
  
  // Allow relative URLs
  if (url.startsWith('/') || url.startsWith('#')) {
    return true;
  }
  
  // Check for allowed protocols
  const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
  try {
    const urlObj = new URL(url);
    return allowedProtocols.includes(urlObj.protocol);
  } catch {
    // If URL parsing fails, it's not a valid URL
    return false;
  }
}

/**
 * Sanitizes user input before sending to the backend
 * This removes any HTML tags and dangerous characters
 * @param input - The user input to sanitize
 * @returns Sanitized plain text string
 */
export function sanitizeUserInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // For user input, we want to preserve the text content but remove any HTML
  // Just remove HTML tags, don't strip all content
  const textOnly = input.replace(/<[^>]*>/g, '');
  
  // Remove dangerous patterns but keep the text
  return textOnly
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
}
